import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  FaCheckCircle,
  FaClock,
  FaExternalLinkAlt,
  FaHeadset,
  FaPaperclip,
  FaSearch,
  FaSpinner,
  FaSyncAlt,
  FaTimes,
  FaUser,
  FaPaperPlane,
  FaPhotoVideo,
  FaExclamationTriangle,
} from "react-icons/fa";
import client from "../api/client";
import "./HelpDesk.css";

const categories = [
  "Order related issue",
  "Product related issue",
  "Inventory issue",
  "Payment issue",
  "Website/app issue",
  "Customer/order problem",
  "Other issue",
];

const statuses = ["Pending", "In Progress", "Done"];

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getEmployeeName = (issue) =>
  issue?.employee?.name || issue?.employee_name || `Employee #${issue?.employee_id || "-"}`;

const statusClass = (status) =>
  String(status || "").toLowerCase().replace(/\s+/g, "-");

const parseGuidedSolution = (value) => {
  if (!value) return null;
  if (Array.isArray(value)) return value;
  const text = String(value).trim();
  if (!text) return null;
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) return parsed.filter((s) => s !== null && s !== undefined && String(s).trim() !== "");
  } catch {
    // fall through
  }
  // If it's plain text with newlines, split it
  if (text.includes("\n")) return text.split("\n").map((l) => l.trim()).filter(Boolean);
  return [text];
};

const isImageMime = (mime) => {
  if (!mime) return false;
  return /^image\//i.test(String(mime));
};

const isImageUrlGuess = (url) => {
  if (!url) return false;
  return /\.(jpe?g|png|webp|gif|bmp|svg)(\?|#|$)/i.test(String(url));
};

const HELP_DESK_STATIC_ORIGIN = "https://techiohisab.com";
const HELP_DESK_STATIC_PREFIX = "/uploads/help-desk/";

const resolveAttachmentUrl = (rawUrl) => {
  if (!rawUrl) return null;
  const value = String(rawUrl).trim();
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;

  const filename = value
    .replace(/^\/api\/help-desk\/uploads\/(help-desk\/)?/, "")
    .replace(/^\/uploads\/help-desk\//, "")
    .replace(/^uploads\/help-desk\//, "")
    .replace(/^help-desk\//, "")
    .replace(/^\//, "");

  const safeName = filename || value.replace(/^\//, "");
  return `${HELP_DESK_STATIC_ORIGIN}${HELP_DESK_STATIC_PREFIX}${safeName}`;
};

const getAttachmentViewUrl = (attachmentId) => {
  if (!attachmentId) return null;
  return `/api/help-desk/attachments/${attachmentId}/view`;
};

const getAttachmentDownloadUrl = (attachmentId) => {
  if (!attachmentId) return null;
  return `/api/help-desk/attachments/${attachmentId}/download`;
};

export default function HelpDesk() {
  const location = useLocation();
  const [issues, setIssues] = useState([]);
  const [summary, setSummary] = useState({ Pending: 0, "In Progress": 0, Done: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const openedNotificationIssue = useRef(null);
  const [filters, setFilters] = useState({
    status: "",
    category: "",
    employeeId: "",
    search: "",
    from: "",
    to: "",
  });
  const [draft, setDraft] = useState({
    status: "Pending",
    admin_response: "",
    admin_note: "",
  });

  // Conversation + reply state
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState("");
  const [replyText, setReplyText] = useState("");
  const [replyAttachment, setReplyAttachment] = useState(null); // { attachment_name, attachment_url, attachment_mime_type }
  const [sendingMessage, setSendingMessage] = useState(false);
  const [replyError, setReplyError] = useState("");
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const replyFileInputRef = useRef(null);

  // Attachment blob URLs state
  const [attachmentBlobUrls, setAttachmentBlobUrls] = useState({});

  // Authenticated attachment loader - fetches protected images as Blobs
  const loadAttachmentBlob = async (attachmentId) => {
    if (!attachmentId) return null;
    try {
      const response = await client.get(`/help-desk/attachments/${attachmentId}/view`, {
        responseType: 'blob',
      });
      const blob = response.data;
      return URL.createObjectURL(blob);
    } catch (err) {
      console.error("Failed to load attachment blob:", err);
      return null;
    }
  };

  // Authenticated attachment download
  const downloadAttachment = async (attachmentId, originalName) => {
    if (!attachmentId) return;
    try {
      const response = await client.get(`/help-desk/attachments/${attachmentId}/download`, {
        responseType: 'blob',
      });
      const blob = response.data;
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = originalName || 'attachment';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Failed to download attachment:", err);
      alert("Failed to download attachment. Please try again.");
    }
  };

  // Authenticated view in new tab
  const viewAttachmentInNewTab = async (attachmentId) => {
    if (!attachmentId) return;
    try {
      const response = await client.get(`/help-desk/attachments/${attachmentId}/view`, {
        responseType: 'blob',
      });
      const blob = response.data;
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      // Note: We don't revoke immediately because the new tab needs to load
      // The browser will clean up when the tab is closed
    } catch (err) {
      console.error("Failed to view attachment:", err);
      alert("Failed to open attachment. Please try again.");
    }
  };

  // Load blob URLs for all attachments when ticket is opened
  useEffect(() => {
    const loadAttachmentBlobs = async () => {
      if (!selectedIssue?.attachments || selectedIssue.attachments.length === 0) {
        setAttachmentBlobUrls({});
        return;
      }

      const newBlobUrls = {};
      for (const attachment of selectedIssue.attachments) {
        if (attachment.id && !attachmentBlobUrls[attachment.id]) {
          const blobUrl = await loadAttachmentBlob(attachment.id);
          if (blobUrl) {
            newBlobUrls[attachment.id] = blobUrl;
          }
        }
      }
      setAttachmentBlobUrls((prev) => ({ ...prev, ...newBlobUrls }));
    };

    loadAttachmentBlobs();

    // Cleanup blob URLs when ticket changes or component unmounts
    return () => {
      Object.values(attachmentBlobUrls).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [selectedIssue?.attachments]);

  const loadIssues = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, value]) => String(value || "").trim())
      );
      const res = await client.get("/help-desk", { params });
      setIssues(res.data?.issues || []);
      setSummary(res.data?.summary || { Pending: 0, "In Progress": 0, Done: 0 });
    } catch (err) {
      console.error("Load help desk issues failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIssues();
  }, []);

  const employeeOptions = useMemo(() => {
    const map = new Map();
    for (const issue of issues) {
      if (issue.employee_id) map.set(issue.employee_id, getEmployeeName(issue));
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [issues]);

  const loadMessages = async (issueId) => {
    setMessagesLoading(true);
    setMessagesError("");
    try {
      const res = await client.get(`/help-desk/${issueId}/messages`);
      const list = Array.isArray(res.data?.messages) ? res.data.messages : [];
      setMessages(list);
    } catch (err) {
      console.error("Load help desk messages failed:", err);
      setMessagesError("Unable to load conversation. Please try again.");
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  };

  const openIssue = async (issue) => {
    try {
      const res = await client.get(`/help-desk/${issue.id}`);
      const nextIssue = res.data;
      setSelectedIssue(nextIssue);
      setDraft({
        status: nextIssue.status || "Pending",
        admin_response: nextIssue.admin_response || "",
        admin_note: nextIssue.admin_note || "",
      });
      // Reset conversation state
      setMessages([]);
      setMessagesError("");
      setReplyText("");
      setReplyAttachment(null);
      setReplyError("");
      setUploadError("");
      void loadMessages(issue.id);
    } catch (err) {
      console.error("Load help desk issue detail failed:", err);
    }
  };

  useEffect(() => {
    const issueId = Number(new URLSearchParams(location.search).get("issueId"));
    if (!Number.isSafeInteger(issueId) || issueId <= 0 || openedNotificationIssue.current === issueId) return;
    const issue = issues.find((item) => Number(item.id) === issueId);
    if (!issue) return;
    openedNotificationIssue.current = issueId;
    void openIssue(issue);
  }, [issues, location.search]);

  const saveStatus = async () => {
    if (!selectedIssue) return;
    setSaving(true);
    try {
      await client.patch(`/help-desk/${selectedIssue.id}/status`, { status: draft.status });
      await loadIssues();
      await openIssue({ id: selectedIssue.id });
    } catch (err) {
      console.error("Update help desk status failed:", err);
    } finally {
      setSaving(false);
    }
  };

  const saveResponse = async () => {
    if (!selectedIssue) return;
    setSaving(true);
    try {
      await client.patch(`/help-desk/${selectedIssue.id}/response`, {
        admin_response: draft.admin_response,
        admin_note: draft.admin_note,
      });
      await loadIssues();
      await openIssue({ id: selectedIssue.id });
    } catch (err) {
      console.error("Update help desk response failed:", err);
    } finally {
      setSaving(false);
    }
  };

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleAddPhoto = () => {
    replyFileInputRef.current?.click();
  };

  const handleFilePicked = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setUploadingAttachment(true);
    setUploadError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await client.post("/help-desk/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data = res.data || {};
      setReplyAttachment({
        attachment_name: data.attachment_name || file.name,
        attachment_url: data.attachment_url,
        attachment_mime_type: data.attachment_mime_type || file.type,
      });
    } catch (err) {
      console.error("Help desk attachment upload failed:", err);
      setUploadError("Unable to upload photo. Please try again.");
      setReplyAttachment(null);
    } finally {
      setUploadingAttachment(false);
    }
  };

  const removeReplyAttachment = () => {
    setReplyAttachment(null);
    setUploadError("");
  };

  const sendMessage = async () => {
    if (!selectedIssue || sendingMessage) return;
    const text = replyText.trim();
    if (!text && !replyAttachment) return;

    setSendingMessage(true);
    setReplyError("");
    try {
      const body = {};
      if (text) body.message = text;
      if (replyAttachment) {
        body.attachment_name = replyAttachment.attachment_name;
        body.attachment_url = replyAttachment.attachment_url;
        body.attachment_mime_type = replyAttachment.attachment_mime_type;
      }
      const res = await client.post(`/help-desk/${selectedIssue.id}/messages`, body);
      const created = res.data?.data;
      setMessages((prev) => (created ? [...prev, created] : prev));
      setReplyText("");
      setReplyAttachment(null);
      setUploadError("");
      // Ensure server state matches
      void loadMessages(selectedIssue.id);
    } catch (err) {
      console.error("Send help desk message failed:", err);
      setReplyError("Unable to send your reply. Please try again.");
    } finally {
      setSendingMessage(false);
    }
  };

  const closeDrawer = () => {
    setSelectedIssue(null);
    setMessages([]);
    setMessagesError("");
    setReplyText("");
    setReplyAttachment(null);
    setReplyError("");
    setUploadError("");
  };

  return (
    <div className="helpdesk-page">
      <header className="helpdesk-header">
        <div className="helpdesk-title-block">
          <div className="helpdesk-logo">
            <FaHeadset />
          </div>
          <div>
            <h1>Help Desk</h1>
            <p>Manage employee support requests</p>
          </div>
        </div>
        <button type="button" className="helpdesk-refresh" onClick={loadIssues}>
          <FaSyncAlt /> Refresh
        </button>
      </header>

      <section className="helpdesk-summary-grid">
        <div className="helpdesk-summary-card pending">
          <FaClock />
          <span>Pending</span>
          <strong>{summary.Pending || 0}</strong>
        </div>
        <div className="helpdesk-summary-card progress">
          <FaSpinner />
          <span>In Progress</span>
          <strong>{summary["In Progress"] || 0}</strong>
        </div>
        <div className="helpdesk-summary-card done">
          <FaCheckCircle />
          <span>Done</span>
          <strong>{summary.Done || 0}</strong>
        </div>
      </section>

      <section className="helpdesk-filters">
        <label className="helpdesk-search">
          <FaSearch />
          <input
            value={filters.search}
            onChange={(e) => updateFilter("search", e.target.value)}
            placeholder="Search issues"
          />
        </label>
        <select value={filters.status} onChange={(e) => updateFilter("status", e.target.value)}>
          <option value="">All statuses</option>
          {statuses.map((status) => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>
        <select value={filters.category} onChange={(e) => updateFilter("category", e.target.value)}>
          <option value="">All categories</option>
          {categories.map((category) => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>
        <select value={filters.employeeId} onChange={(e) => updateFilter("employeeId", e.target.value)}>
          <option value="">All employees</option>
          {employeeOptions.map((employee) => (
            <option key={employee.id} value={employee.id}>{employee.name}</option>
          ))}
        </select>
        <input type="date" value={filters.from} onChange={(e) => updateFilter("from", e.target.value)} />
        <input type="date" value={filters.to} onChange={(e) => updateFilter("to", e.target.value)} />
        <button type="button" onClick={loadIssues}>Apply</button>
      </section>

      <section className="helpdesk-table-card">
        {loading ? (
          <div className="helpdesk-empty">Loading help desk issues...</div>
        ) : issues.length ? (
          <div className="helpdesk-table-wrap">
            <table className="helpdesk-table">
              <thead>
                <tr>
                  <th>Issue ID</th>
                  <th>Employee</th>
                  <th>Category</th>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Updated</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {issues.map((issue) => (
                  <tr key={issue.id}>
                    <td>#{issue.id}</td>
                    <td>{getEmployeeName(issue)}</td>
                    <td>{issue.category}</td>
                    <td>{issue.title}</td>
                    <td><span className={`helpdesk-badge ${statusClass(issue.status)}`}>{issue.status}</span></td>
                    <td>{formatDate(issue.created_at)}</td>
                    <td>{formatDate(issue.updated_at)}</td>
                    <td>
                      <button type="button" className="helpdesk-view-btn" onClick={() => openIssue(issue)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="helpdesk-empty">No help desk issues found.</div>
        )}
      </section>

      {selectedIssue && (
        <div className="helpdesk-drawer-overlay" onClick={closeDrawer}>
          <aside className="helpdesk-drawer" onClick={(e) => e.stopPropagation()}>
            {/* 1. HEADER */}
            <div className="helpdesk-drawer-header">
              <div>
                <span>Issue #{selectedIssue.id}</span>
                <h2>{selectedIssue.title}</h2>
              </div>
              <button type="button" onClick={closeDrawer} aria-label="Close help desk detail">
                <FaTimes />
              </button>
            </div>

            {/* 2. META GRID */}
            <div className="helpdesk-detail-grid">
              <div className="helpdesk-detail-item">
                <span>Employee</span>
                <strong><FaUser /> {getEmployeeName(selectedIssue)}</strong>
                <small>{selectedIssue.employee?.email || "-"}</small>
              </div>
              <div className="helpdesk-detail-item">
                <span>Category</span>
                <strong>{selectedIssue.category}</strong>
              </div>
              <div className="helpdesk-detail-item">
                <span>Created</span>
                <strong>{formatDate(selectedIssue.created_at)}</strong>
              </div>
              <div className="helpdesk-detail-item">
                <span>Current Status</span>
                <strong><span className={`helpdesk-badge ${statusClass(selectedIssue.status)}`}>{selectedIssue.status}</span></strong>
              </div>
            </div>

            {/* 3. DESCRIPTION */}
            <div className="helpdesk-detail-section">
              <h3>Problem</h3>
              <h4 style={{ margin: "0 0 8px", fontSize: "15px" }}>{selectedIssue.title}</h4>
              <p>{selectedIssue.description}</p>
            </div>

            {/* 4. EMPLOYEE HELP FLOW */}
            <div className="helpdesk-detail-section">
              <h3>Employee Help Flow</h3>
              <div className="helpdesk-helpflow-grid">
                <div>
                  <span className="helpdesk-helpflow-label">Topic / Category</span>
                  <div className="helpdesk-helpflow-value">{selectedIssue.category}</div>
                </div>
                <div>
                  <span className="helpdesk-helpflow-label">Selected Question</span>
                  <div className="helpdesk-helpflow-value">
                    {selectedIssue.selected_question_title || "Not available"}
                  </div>
                </div>
                <div className="helpdesk-helpflow-full">
                  <span className="helpdesk-helpflow-label">Guided Solution / Steps</span>
                  <GuidedSolutionDisplay value={selectedIssue.guided_solution} />
                </div>
              </div>
            </div>

            {/* 5. ORIGINAL ATTACHMENTS */}
            {selectedIssue.attachments && selectedIssue.attachments.length > 0 ? (
              <div className="helpdesk-detail-section">
                <h3>Original Attachment{selectedIssue.attachments.length > 1 ? "s" : ""}</h3>
                {selectedIssue.attachments.map((attachment) => {
                  const blobUrl = attachmentBlobUrls[attachment.id];
                  const isImage = isImageMime(attachment.mime_type) || isImageUrlGuess(attachment.file_url);
                  return (
                    <div key={attachment.id} className="helpdesk-attachment-card">
                      <div className="helpdesk-attachment-header">
                        <FaPaperclip />
                        <span className="helpdesk-attachment-filename">{attachment.original_name}</span>
                      </div>
                      {isImage ? (
                        blobUrl ? (
                          <div className="helpdesk-attachment-preview-wrapper">
                            <img
                              className="helpdesk-image-preview"
                              src={blobUrl}
                              alt={attachment.original_name}
                              onClick={() => viewAttachmentInNewTab(attachment.id)}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                                e.currentTarget.parentElement?.classList.add("helpdesk-preview-error");
                              }}
                            />
                          </div>
                        ) : (
                          <div className="helpdesk-attachment-preview-wrapper">
                            <span className="helpdesk-preview-error">Loading preview...</span>
                          </div>
                        )
                      ) : null}
                      <div className="helpdesk-attachment-actions">
                        <button
                          type="button"
                          className="helpdesk-attachment-btn"
                          onClick={() => viewAttachmentInNewTab(attachment.id)}
                        >
                          <FaExternalLinkAlt /> View Full Size
                        </button>
                        <button
                          type="button"
                          className="helpdesk-attachment-btn"
                          onClick={() => downloadAttachment(attachment.id, attachment.original_name)}
                        >
                          Download
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (selectedIssue.attachment_name || selectedIssue.attachment_url) ? (
              <div className="helpdesk-detail-section">
                <h3>Original Attachment (Legacy)</h3>
                {(selectedIssue.attachment_url &&
                  (isImageMime(selectedIssue.attachment_mime_type) ||
                   isImageUrlGuess(selectedIssue.attachment_url))) ? (
                  <a
                    className="helpdesk-attachment-link"
                    href={resolveAttachmentUrl(selectedIssue.attachment_url)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img
                      className="helpdesk-image-preview"
                      src={resolveAttachmentUrl(selectedIssue.attachment_url)}
                      alt={selectedIssue.attachment_name || "Employee attachment"}
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  </a>
                ) : null}
                <div className="helpdesk-attachment">
                  <FaPaperclip />
                  <span>{selectedIssue.attachment_name || selectedIssue.attachment_url || "Attachment"}</span>
                  {selectedIssue.attachment_url ? (
                    <a href={resolveAttachmentUrl(selectedIssue.attachment_url)} target="_blank" rel="noreferrer">
                      <FaExternalLinkAlt />
                    </a>
                  ) : (
                    <span className="helpdesk-no-preview">No preview available</span>
                  )}
                </div>
              </div>
            ) : null}

            {/* 6. CONVERSATION TIMELINE */}
            <div className="helpdesk-detail-section">
              <h3>Conversation</h3>
              {messagesLoading && (
                <div className="helpdesk-empty helpdesk-empty-inline">Loading conversation...</div>
              )}
              {!messagesLoading && messagesError && (
                <div className="helpdesk-error-inline">
                  <FaExclamationTriangle /> {messagesError}
                </div>
              )}
              {!messagesLoading && !messagesError && messages.length === 0 && (
                <div className="helpdesk-empty helpdesk-empty-inline">No conversation messages yet.</div>
              )}
              {!messagesLoading && messages.length > 0 && (
                <div className="helpdesk-message-list">
                  {messages.map((message) => (
                    <ConversationMessage key={message.id} message={message} />
                  ))}
                </div>
              )}
            </div>

            {/* 7. REPLY TO EMPLOYEE */}
            <div className="helpdesk-detail-section helpdesk-reply-panel">
              <h3>Reply to Employee</h3>
              <textarea
                className="helpdesk-reply-textarea"
                rows={3}
                placeholder="Type your reply to the employee..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                disabled={sendingMessage}
              />

              <input
                ref={replyFileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFilePicked}
              />

              <div className="helpdesk-reply-actions">
                <div className="helpdesk-reply-left">
                  <button
                    type="button"
                    className="helpdesk-attach-btn"
                    onClick={handleAddPhoto}
                    disabled={uploadingAttachment || sendingMessage || !!replyAttachment}
                  >
                    {uploadingAttachment ? (
                      <><FaSpinner className="spin" /> Uploading...</>
                    ) : (
                      <><FaPhotoVideo /> Add Photo</>
                    )}
                  </button>
                  {replyAttachment && (
                    <div className="helpdesk-attachment-preview">
                      <FaPaperclip />
                      <span className="helpdesk-attachment-preview-name">
                        {replyAttachment.attachment_name}
                      </span>
                      <button
                        type="button"
                        className="helpdesk-attachment-preview-remove"
                        onClick={removeReplyAttachment}
                        aria-label="Remove attachment"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="helpdesk-send-btn"
                  onClick={sendMessage}
                  disabled={sendingMessage || (!replyText.trim() && !replyAttachment)}
                >
                  {sendingMessage ? (
                    <><FaSpinner className="spin" /> Sending...</>
                  ) : (
                    <><FaPaperPlane /> Send</>
                  )}
                </button>
              </div>

              {uploadError && <div className="helpdesk-error-text"><FaExclamationTriangle /> {uploadError}</div>}
              {replyError && <div className="helpdesk-error-text"><FaExclamationTriangle /> {replyError}</div>}
            </div>

            {/* 8. STATUS */}
            <div className="helpdesk-action-panel">
              <label>
                Status
                <select value={draft.status} onChange={(e) => setDraft((prev) => ({ ...prev, status: e.target.value }))}>
                  {statuses.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </label>
              <button type="button" onClick={saveStatus} disabled={saving}>
                {saving ? "Saving..." : "Save Status"}
              </button>
            </div>

            {/* 9. RESOLUTION */}
            <div className="helpdesk-action-panel stacked">
              <label>
                Admin Response (Final Resolution)
                <textarea
                  value={draft.admin_response}
                  onChange={(e) => setDraft((prev) => ({ ...prev, admin_response: e.target.value }))}
                  rows={4}
                />
              </label>
              <label>
                Internal Admin Note
                <textarea
                  value={draft.admin_note}
                  onChange={(e) => setDraft((prev) => ({ ...prev, admin_note: e.target.value }))}
                  rows={4}
                />
              </label>
              <button type="button" onClick={saveResponse} disabled={saving}>
                {saving ? "Saving..." : "Save Response"}
              </button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function GuidedSolutionDisplay({ value }) {
  const steps = parseGuidedSolution(value);
  if (!steps || steps.length === 0) {
    return <div className="helpdesk-helpflow-value helpdesk-not-available">Not available</div>;
  }
  return (
    <ol className="helpdesk-guided-steps">
      {steps.map((step, idx) => (
        <li key={idx}>{String(step)}</li>
      ))}
    </ol>
  );
}

function ConversationMessage({ message }) {
  const isAdmin = message.sender_type === "admin";
  const showImage =
    message.attachment_url &&
    (isImageMime(message.attachment_mime_type) || isImageUrlGuess(message.attachment_url));
  const resolvedAttachmentUrl = resolveAttachmentUrl(message.attachment_url);

  return (
    <div className={`helpdesk-message ${isAdmin ? "helpdesk-message--admin" : "helpdesk-message--employee"}`}>
      <div className="helpdesk-message-meta">
        <span className={`helpdesk-message-label ${isAdmin ? "label-admin" : "label-employee"}`}>
          {isAdmin ? "Admin" : "Employee"}
        </span>
        <span className="helpdesk-message-sender">{message.sender_name || `User #${message.sender_id || "?"}`}</span>
        <span className="helpdesk-message-time">{formatDate(message.created_at)}</span>
      </div>
      {message.message && (
        <div className="helpdesk-message-text">{message.message}</div>
      )}
      {message.attachment_url && (
        <div className="helpdesk-message-attachment">
          {showImage ? (
            <a href={resolvedAttachmentUrl} target="_blank" rel="noreferrer" className="helpdesk-attachment-link">
              <img
                className="helpdesk-image-preview helpdesk-image-preview-small"
                src={resolvedAttachmentUrl}
                alt={message.attachment_name || "Attachment"}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            </a>
          ) : null}
          <div className="helpdesk-message-attachment-meta">
            <FaPaperclip />
            <span>{message.attachment_name || message.attachment_url || "Attachment"}</span>
            <a href={resolvedAttachmentUrl} target="_blank" rel="noreferrer" aria-label="Open attachment">
              <FaExternalLinkAlt />
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
